import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  Printer,
  BarChart3,
  FileText,
  UserCog,
  UserCheck,
  X,
  Cog,
  Palette,
  Mail,
  MessageSquare,
  Building,
  Shield,
  Tag,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useEffectiveAuth } from '../../hooks/useEffectiveAuth';

const Sidebar = ({ isOpen, onClose }) => {
  const { effectiveUser, effectivePermissions } = useEffectiveAuth();
  
  // Get navigation items based on user permissions
  const getNavigationItems = () => {
    const items = [
      { 
        to: '/dashboard', 
        icon: LayoutDashboard, 
        label: 'Dashboard',
        show: true // Everyone can see dashboard
      }
    ];

    // Events - for admins and event admins
    if (effectivePermissions.canCreateEvents() || effectivePermissions.canSeeAllEvents()) {
      items.push({
        to: '/events', 
        icon: Calendar, 
        label: 'Events',
        show: true
      });
    }

    // Event Settings (Custom Fields, Badges, Stickers, QR) - for admins
    if (effectivePermissions.isAdmin()) {
      items.push({
        to: '/event-settings', 
        icon: Settings, 
        label: 'Event Settings',
        show: true
      });
    }

    // Attendees - for all staff roles
    if (effectiveUser?.role !== 'attendee') {
      items.push({
        to: '/attendees', 
        icon: Users, 
        label: 'Attendees',
        show: true
      });
    }

    // Print Station - for all staff roles
    if (effectiveUser?.role !== 'attendee') {
      items.push({
        to: '/print-station', 
        icon: Printer, 
        label: 'Print Station',
        show: true
      });
    }

    // Check-In Station - for all staff roles
    if (effectiveUser?.role !== 'attendee') {
      items.push({
        to: '/check-in', 
        icon: UserCheck, 
        label: 'Check-In Station',
        show: true
      });
    }

    // User Management - only for users who can create users
    if (effectivePermissions.canCreateUsers()) {
      items.push({
        to: '/users', 
        icon: UserCog, 
        label: 'User Management',
        show: true
      });
    }

    // Super Admin specific pages
    if (effectiveUser?.role === 'super_admin') {
      items.push({
        to: '/organizations',
        icon: Building,
        label: 'Organizations',
        show: true
      });
      items.push({
        to: '/administrators',
        icon: Shield,
        label: 'Administrators',
        show: true
      });
    }

    // Campaigns (Email & SMS) - for admins
    if (effectivePermissions.isAdmin()) {
      items.push({
        to: '/campaigns', 
        icon: Mail, 
        label: 'Campaigns',
        show: true
      });
    }

    // Analytics - for admins
    if (effectivePermissions.isAdmin()) {
      items.push({
        to: '/analytics', 
        icon: BarChart3, 
        label: 'Analytics',
        show: true
      });
    }

    // Help / User Guide - available to everyone
    items.push({
      to: '/help-guide',
      icon: FileText,
      label: 'Help / User Guide',
      show: true
    });

    // Organization Settings - for event admins
    if (effectivePermissions.isEventAdmin() || effectivePermissions.isSuperAdmin()) {
      items.push({
        to: '/organization-settings', 
        icon: Cog, 
        label: 'Organization Settings',
        show: true
      });
    }

    // System Settings - for super admins only
    if (effectivePermissions.isSuperAdmin()) {
      items.push({
        to: '/system-settings', 
        icon: Cog, 
        label: 'System Settings',
        show: true
      });
    }

    return items.filter(item => item.show);
  };

  const navItems = getNavigationItems();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed md:sticky top-0 left-0 z-50 bg-white h-screen transition-all duration-300 ease-in-out flex-shrink-0',
          isOpen 
            ? 'w-64 border-r border-gray-200 translate-x-0' 
            : 'w-64 md:w-0 -translate-x-full md:translate-x-0 border-r-0 overflow-hidden'
        )}
      >
        {/* Inner container to prevent content squeezing during transition */}
        <div className="w-64 h-full flex flex-col">
          {/* Mobile close button */}
          <div className="md:hidden flex justify-end p-4 border-b border-gray-100">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
          </div>

          <nav className="p-4 space-y-1 overflow-y-auto flex-1 pb-20">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => onClose()}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
